import React, { useCallback, useEffect, useMemo } from 'react'
import { useField, useFormikContext } from 'formik'
import { TextListGroupContainer } from './style'
import { useNewTranslation } from 'hooks'
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd'
import { reorderList } from 'components/utils'
import RectangleButton from 'particles/Button/RectangleButton'
import Label, { LabelSize, LinkConfig } from 'particles/Label'
import { generateUuid } from 'services/uuid'
import TextList from '.'
import Text from 'particles/Text'
import CommentContainer from 'components/Shared/Comment'
import { IconName, IconSize } from 'particles/Icon'
import { useDebounce } from 'hooks'

export interface FormikTextListGroupProps {
    width?: string
    title?: string
    subtitle?: string
    isRequired?: boolean
    name?: string
    placeholder?: string
    buttonText?: string
    isDefault?: boolean
    className?: string
    isReadOnly?: boolean
    isDraggable?: boolean
    identifier?: string
    textFieldSize?: 'm' | 's'
    isCommentable?: boolean
    isCommented?: boolean
    isDisabled?: boolean
    inputIcon?: IconName
    inputIconSize?: IconSize
    dataTestId?: string
    labelSize?: LabelSize
    link?: LinkConfig
    tooltip?: string
    showError?: boolean
    maxItem?: number
    isEditableDefaultTag?: boolean
    tooltipContext?: { title?: string; label?: string }
}

interface TextListGroupProps extends FormikTextListGroupProps {
    onAddItem: () => void
    onChange?: (value: string, index: number) => void
    onDeleteItem?: (index: number) => void
    onDragEnd: (result: DropResult) => void
    onCommentClick?: (e: React.MouseEvent<HTMLDivElement>) => void
    fieldValue: ({ uuid: string; value: string; is_default?: boolean } | undefined)[]
    hasError?: boolean
    error?: string | undefined
}

const TextListGroup: React.FC<TextListGroupProps> = ({
    className,
    name = '',
    placeholder = '',
    width,
    title,
    subtitle,
    isRequired = false,
    buttonText,
    isReadOnly = false,
    isDraggable = true,
    isEditableDefaultTag = false,
    tooltipContext,
    onAddItem,
    onChange,
    onDeleteItem,
    onCommentClick,
    onDragEnd,
    fieldValue,
    textFieldSize,
    hasError,
    error,
    isCommentable,
    isCommented,
    isDisabled,
    inputIcon,
    inputIconSize,
    identifier,
    isDefault,
    dataTestId,
    labelSize,
    link,
    tooltip,
    maxItem,
}) => {
    const [t] = useNewTranslation()
    return (
        <TextListGroupContainer className={className} width={width}>
            {(title || subtitle) && (
                <CommentContainer identifier={identifier}>
                    <Label
                        title={title || ''}
                        subtext={subtitle}
                        isRequired={isRequired}
                        size={labelSize}
                        link={link}
                        tooltip={tooltip}
                    />
                </CommentContainer>
            )}
            <div className="list-item-field">
                <DragDropContext
                    onDragEnd={(result): void => {
                        onDragEnd?.(result)
                    }}
                >
                    <Droppable droppableId="droppable">
                        {(provided): React.ReactElement => (
                            <div ref={provided.innerRef} {...provided.droppableProps}>
                                {fieldValue.map(
                                    (
                                        value: { uuid: string; value: string; is_default?: boolean } | undefined,
                                        index: number,
                                    ) => {
                                        const fieldName = `${name}[${index}]`
                                        const tooltipContextDisplay =
                                            tooltipContext ??
                                            (!isEditableDefaultTag && value?.is_default
                                                ? {
                                                      label: t(
                                                          value.value.includes('partipost')
                                                              ? 'tooltip.partipostRelatedIndelibleTag'
                                                              : 'tooltip.generalIndelibleTag',
                                                      ),
                                                  }
                                                : undefined)

                                        return (
                                            <Draggable
                                                key={value?.uuid || index}
                                                index={index}
                                                draggableId={value?.uuid || index.toString()}
                                            >
                                                {(draggableProvided, snapshot): JSX.Element => (
                                                    <CommentContainer identifier={`${value?.uuid}_${identifier}`}>
                                                        <TextList
                                                            name={fieldName}
                                                            placeholder={placeholder}
                                                            onDelete={e => {
                                                                e.preventDefault()
                                                                onDeleteItem?.(index)
                                                            }}
                                                            value={value?.value ?? ''}
                                                            onChange={inputValue => {
                                                                onChange?.(inputValue, index)
                                                            }}
                                                            onCommentClick={onCommentClick}
                                                            isDeletable={!isDefault || fieldValue.length > 1}
                                                            isReadOnly={isReadOnly}
                                                            isDraggable={isDraggable}
                                                            ref={draggableProvided.innerRef}
                                                            draggableProvided={draggableProvided}
                                                            snapshot={snapshot}
                                                            size={textFieldSize}
                                                            isCommentable={isCommentable}
                                                            isCommented={isCommented}
                                                            isDisabled={
                                                                isDisabled ||
                                                                (value?.is_default && !isEditableDefaultTag)
                                                            }
                                                            inputIcon={inputIcon}
                                                            showError={hasError && !!error}
                                                            inputIconSize={inputIconSize}
                                                            dataTestId={dataTestId}
                                                            tooltipContext={tooltipContextDisplay}
                                                            {...draggableProvided.draggableProps}
                                                        />
                                                    </CommentContainer>
                                                )}
                                            </Draggable>
                                        )
                                    },
                                )}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
                {hasError && error ? (
                    <Text data-testid={`${dataTestId}_error`} color="error" scale="caption400">
                        {error}
                    </Text>
                ) : null}
            </div>
            {!isReadOnly && (
                <RectangleButton
                    form="outlined"
                    size="s"
                    label={buttonText ? buttonText : t('button.addDo')}
                    data-testid={`test-button-add-${name}`}
                    icon="plus"
                    onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                        e.preventDefault()
                        onAddItem && onAddItem()
                    }}
                    isDisabled={!!maxItem && fieldValue.length >= maxItem}
                    className="width-fit-content"
                />
            )}
        </TextListGroupContainer>
    )
}
export default TextListGroup

export type TextListGroupValue = { uuid: string; value: string } | undefined

const FormikTextListGroup: React.FC<FormikTextListGroupProps> = ({
    name = '',
    title,
    subtitle,
    isDefault = false,
    identifier,
    showError,
    ...props
}) => {
    const [field, meta] = useField<TextListGroupValue[]>(name)
    const { setFieldValue, setFieldTouched } = useFormikContext()

    const fieldValue = useMemo(() => {
        return field.value ? field.value : isDefault ? [{ uuid: generateUuid(), value: '' }] : []
    }, [field.value, isDefault])

    const handleChange = ({ newValue, index }: { newValue: string; index: number }) => {
        setFieldTouched(name, true)
        if (fieldValue[index]) {
            setFieldValue(`${name}[${index}]`, {
                uuid: fieldValue[index]?.uuid,
                value: newValue,
            })
        }
    }

    const onChangeDebounce = useDebounce(handleChange, 200)

    useEffect(() => {
        if (fieldValue.length === 0) {
            handleAddItem()
        }
    }, [fieldValue])

    const handleAddItem = useCallback(() => {
        setFieldValue(name, [...fieldValue, { uuid: generateUuid(), value: '' }])
    }, [fieldValue, name, setFieldValue])

    const handleRemoveItem = useCallback(
        index => {
            const newItems = fieldValue.filter((_, i) => i !== index)
            setFieldValue(name, newItems)
            if (newItems.length === 0) setFieldTouched(field.name, true, false)
        },
        [fieldValue, name, setFieldValue],
    )

    const handleDragEnd = useCallback(
        (result: DropResult) => {
            if (!result.destination || result.destination.index === result.source.index) return

            const items = reorderList([...fieldValue], result.source.index, result.destination.index)

            setFieldValue(field.name, items)
        },
        [fieldValue],
    )

    return (
        <TextListGroup
            {...props}
            title={title}
            subtitle={subtitle}
            fieldValue={fieldValue}
            onAddItem={handleAddItem}
            onDeleteItem={handleRemoveItem}
            onChange={(value, index) => onChangeDebounce({ newValue: value, index })}
            onDragEnd={handleDragEnd}
            hasError={showError ?? Boolean(meta.touched && meta.error)}
            error={meta.error}
            identifier={identifier}
            isDefault={isDefault}
        />
    )
}

export { FormikTextListGroup }
